import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import {
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { ReactstrapInput } from "reactstrap-formik";
import * as Yup from "yup";

import config from "../../store/config";
import { configuratorActions } from "../../store/configurator-slice";

function BillingLoginForm({ isOpen, onClose, onLoginSuccess }) {
  const dispatch = useDispatch();
  const formRef = useRef();

  const [loginError, setLoginError] = useState("");
  const [passwordLabel, setPasswordLabel] = useState("Send Password Reset Email");
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [forgotPasswordErrorMsg, setForgotPasswordErrorMsg] = useState("");
  const [forgotPasswordConfirmMsg, setForgotPasswordConfirmMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formSchema = Yup.object().shape({
    emailAddress: Yup.string().required("Required"),
    password: Yup.string().required("Required"),
  });

  const showResetPassword = (e) => {
    e.preventDefault();
    setResetPasswordVisible(true);
  };

  const submitForgotPassword = async () => {
    setForgotPasswordErrorMsg("");

    if (formRef.current.values.emailAddress === "") {
      setForgotPasswordErrorMsg("Please enter a valid email address.");
      return;
    }

    setPasswordLabel("Please wait...");
    await sendForgotPassword(formRef.current.values.emailAddress);
    setForgotPasswordConfirmMsg(
      "Please check your email for a link to reset your password. Do not close this window. After you reset your password, you will be able to sign in here."
    );
    setPasswordLabel("Send Password Reset Email");
  };

  async function sendForgotPassword(emailAddress) {
    const body = {
      Email: emailAddress,
      __RequestVerificationToken: window.AntiForgeryToken,
    };

    try {
      const response = await fetch(
        config.settings.apiEndpoint + "api/requests/SendPasswordReset",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-TOKEN": window.AntiForgeryToken,
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        return { success: false, error: "" };
      }

      return { success: true };
    } catch (err) {
      setForgotPasswordErrorMsg("Password reset request error: " + err.message);
      return { success: false, error: err.message };
    }
  }

  async function formSubmitHandler(values) {
    setLoginError("");
    setIsSubmitting(true);

    const credentials = {
      EmailAddress: values.emailAddress,
      Password: values.password,
    };

    try {
      const response = await fetch(
        config.settings.apiEndpoint + "api/requests/SignIn",
        {
          method: "POST",
          credentials: "include",
          body: JSON.stringify(credentials),
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-TOKEN": window.AntiForgeryToken,
          },
        }
      );

      const data = await response.json();

      if (!response.ok || !data.isAuthenticated) {
        setLoginError("Your login information was not valid.");
        setIsSubmitting(false);
        return;
      }

      if (data.antiForgeryToken) {
        window.AntiForgeryToken = data.antiForgeryToken;
      }

      dispatch(configuratorActions.setLoggedInDonor(data));
      onLoginSuccess(data.donorInfo);
    } catch (err) {
      setLoginError("Your login information was not valid.");
      setIsSubmitting(false);
    }
  }

  const handleClose = () => {
    setLoginError("");
    setResetPasswordVisible(false);
    setForgotPasswordErrorMsg("");
    setForgotPasswordConfirmMsg("");
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md" backdrop="static">
      <ModalHeader toggle={handleClose}>Sign In to Continue</ModalHeader>
      <ModalBody>
        <p>Please sign in so your billing address can be used for this request.</p>
        {loginError && (
          <p style={{ color: "#ff0000" }}>{loginError}</p>
        )}
        <Formik
          initialValues={{
            emailAddress: "",
            password: "",
          }}
          validationSchema={formSchema}
          onSubmit={formSubmitHandler}
          innerRef={formRef}
        >
          {({ handleBlur }) => (
            <Form className="text-start">
              <Container className="cfg-form-section">
                <Row className="mb-4">
                  <Col xs="12">
                    <label className="required" htmlFor="billingLoginEmail">
                      Email Address:
                    </label>
                    <Field
                      name="emailAddress"
                      id="billingLoginEmail"
                      component={ReactstrapInput}
                      onBlur={handleBlur}
                      label={false}
                    />
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col xs="12">
                    <label className="required" htmlFor="billingLoginPassword">
                      Password:
                    </label>
                    <Field
                      name="password"
                      id="billingLoginPassword"
                      type="password"
                      component={ReactstrapInput}
                      onBlur={handleBlur}
                      label={false}
                    />
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col xs="12">
                    <Button
                      type="submit"
                      size="sm"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Signing In..." : "Log In"}
                    </Button>

                    {!resetPasswordVisible && (
                      <a
                        href="#"
                        onClick={showResetPassword}
                        style={{ marginLeft: "20px" }}
                      >
                        Forgot your password?
                      </a>
                    )}
                    {resetPasswordVisible && (
                      <Button
                        onClick={submitForgotPassword}
                        style={{ marginLeft: "20px" }}
                        type="button"
                        size="sm"
                        color="secondary"
                      >
                        {passwordLabel}
                      </Button>
                    )}

                    {forgotPasswordErrorMsg !== "" && (
                      <div
                        className="alert alert-danger"
                        style={{ margin: "20px 0" }}
                        role="alert"
                      >
                        {forgotPasswordErrorMsg}
                      </div>
                    )}
                    {forgotPasswordConfirmMsg !== "" && (
                      <div
                        className="alert alert-success"
                        style={{ margin: "20px 0" }}
                        role="alert"
                      >
                        {forgotPasswordConfirmMsg}
                      </div>
                    )}
                  </Col>
                </Row>
              </Container>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
}

export default BillingLoginForm;
